import { Button } from './Button';

// ------------------------------------------------------------------
// LoadMorePager — story 2-147tb (P10 §2.7 row 7, "Pagination" / "result
// counts"). Closes the row's own named gap verbatim: "no dedicated
// Pagination component exists; converged by 2-147tb" and "result-count text
// is ad hoc per page today — same not-yet-converged category as
// pagination, 2-147tb's job".
//
// ── THE MEASURED DUPLICATION THIS REPLACES ─────────────────────────────
// Every real cursor-paged collection in the tree hand-rolled the SAME
// footer markup independently — a centered "Load more" button above an
// `aria-live="polite"` "Showing N[ of M]" paragraph, `data-testid=
// "cursor-list-count"` — confirmed by reading all seven call sites, not
// assumed: `SitesPage.tsx`, `ReportsListPage.tsx`, `TemplateLibraryPage.tsx`,
// `ItemsListPage.tsx`, `ActivityPage.tsx`, `ReviewQueuePage.tsx`,
// `InspectionTypesPage.tsx`. Several of those files' own comments already
// name story `2-147h` as the point they converged onto this exact SHAPE by
// hand ("converged onto the shared stacked/centered footer layout... every
// other collection page uses") — but no shared component ever existed, so
// the convergence had to be re-typed, byte-for-byte, into a seventh file
// every time a new collection page shipped. This component is that missing
// single source of truth; this story also migrates all seven real call
// sites onto it (see SESSION-HANDOFF.md for the full list).
//
// ── LOADING STATE: standardized on `Button`'s own `loading` prop ────────
// Two shapes existed at HEAD: `disabled={isLoadingMore}` + a manual text
// swap (`isLoadingMore ? 'Loading…' : 'Load more'`) on three pages, and
// `loading={isLoadingMore}` with a static label on two. `Button`'s `loading`
// prop already gives a spinner + `aria-busy` + implicit disable (see
// `Button.tsx`) — strictly more than the manual text swap communicates, and
// the label stays stable instead of retext-ing under a screen reader mid
// fetch. This component standardizes on `loading`, the more complete of the
// two pre-existing shapes.
//
// ── WHY NOT A NUMBERED PAGER ────────────────────────────────────────────
// Every real consumer (the seven above) is a cursor list — `useCursorList`
// (G-18) or its client-side `page`-counts-"Load more"-clicks presentation
// equivalent (`ItemsListPage`/`ActivityPage`, neither has a server cursor to
// page against). None has a "jump to page 7" need or a server that could
// answer one. A page-number pager would be speculative abstraction with no
// real consumer — the same rule-of-three discipline `ActionHosts.tsx`
// already applied to decline an untested `href` field. If a real numbered-
// pager consumer appears later, it is a second, differently-named component
// (this one's contract — "there is a NEXT page, fetch it" — does not
// generalise to "jump to page N" without becoming a different control).
//
// ── URL-STATE CONTRACT (G-25) — NOT owned by this component ─────────────
// This component is presentational: it renders a button and a count from
// props and calls `onLoadMore` on click. Whether that click bumps a
// `useUrlState` `page` field (`TemplateLibraryPage`, `ItemsListPage`,
// `ActivityPage` — all URL-owned, restored on reload/back per G-25) or
// calls `useCursorList`'s own `fetchNextPage()` directly (`SitesPage`,
// `ReportsListPage`, `ReviewQueuePage`, `InspectionTypesPage` — cursor state
// lives in react-query's cache, not the URL) is the CALLER's decision,
// unchanged by this refactor — this story converges the render, not the
// state-ownership pattern, and neither pre-existing pattern violates G-25
// (a cursor is opaque server paging state, not filter/sort/page facts a
// deep link needs to reproduce).

export interface LoadMorePagerProps {
  /** Records rendered so far — the flattened, already-fetched (or already-sliced) set. */
  shownCount: number;
  /**
   * Full filtered collection size, when the endpoint (or client-side
   * computation) reports it. Omitted → "Showing N" with no "of M".
   * `| undefined` (not just optional) so a caller can forward
   * `useCursorList`'s own `number | undefined` `total` straight through —
   * exactOptionalPropertyTypes treats "explicitly undefined" and "absent"
   * as different types, the same gotcha this chain's other stories
   * (2-147sb/2-147ta) already documented and worked around the same way.
   */
  total?: number | undefined;
  /** Another page exists to fetch/reveal. */
  hasMore: boolean;
  /** Fetches (or reveals) the next page. */
  onLoadMore: () => void;
  /** A subsequent page is in flight. Forwarded to `Button`'s own `loading` (spinner + aria-busy + implicit disable). */
  isLoadingMore?: boolean;
  /** @default 'Load more' */
  loadMoreLabel?: string;
  className?: string;
  /** @default 'cursor-list-count' — the pre-existing hook every consumer's own footer already carried; kept stable rather than renamed. */
  'data-testid'?: string;
}

export function LoadMorePager({
  shownCount,
  total,
  hasMore,
  onLoadMore,
  isLoadingMore = false,
  loadMoreLabel = 'Load more',
  className = '',
  'data-testid': testId = 'cursor-list-count',
}: LoadMorePagerProps): JSX.Element {
  return (
    <div className={`flex flex-col items-center gap-2 pt-2 ${className}`}>
      {hasMore && (
        <Button variant="secondary" loading={isLoadingMore} onClick={onLoadMore}>
          {loadMoreLabel}
        </Button>
      )}
      <p className="text-xs text-muted-foreground" aria-live="polite" data-testid={testId}>
        Showing {shownCount}
        {typeof total === 'number' ? ` of ${total}` : ''}
      </p>
    </div>
  );
}
